/**
 * SignInPageNew — Google OAuth-only sign-in page.
 *
 * First impression of the app: "Apple meets Disney Parks" — clean base with
 * subtle purple/gold accents that signal delight without visual noise.
 *
 * Design decisions:
 * - Purple tint background (light) / dark surface (dark) — premium depth
 * - Sparkling paper airplane — "subtle magic" per design brief
 * - Four-color Google G on the button — Google Brand Guidelines compliance
 * - Welcome heading + value subtitle — warms the entry, communicates purpose
 */
import { useEffect, useState } from 'react';
import { Timer } from 'lucide-react';

function SparkleIcon({ className = '' }: { className?: string }) {
  return (
    <svg
      className={`${className}`}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 0L14.24 8.76L23 12L14.24 15.24L12 24L9.76 15.24L1 12L9.76 8.76L12 0Z"
        fill="var(--disney-gold-500)"
        stroke="var(--disney-gold-400)"
        strokeWidth="0.5"
      />
    </svg>
  );
}

// function PaperPlaneIcon({
//   className = '',
// }: {
//   className?: string;
// }) {
//   return (
//     <svg
//       className={`${className}`}
//       viewBox="0 0 64 64"
//       fill="none"
//       xmlns="http://www.w3.org/2000/svg"
//     >
//       <path
//         d="M8 56L56 8L36 36L8 8L8 28L36 56L8 56Z"
//         stroke="white"
//         strokeWidth="3"
//         strokeLinecap="round"
//         strokeLinejoin="round"
//       />
//     </svg>
//   );
// }

function GoogleIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={`${className}`} viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

export function SignInPageNew(): React.ReactElement {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  const handleOAuth = () => {
    window.location.href =
      `${process.env.REACT_APP_API_BASE || ''}/oauth2/authorization/google`;
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center overflow-hidden relative">
      {/* Subtle purple tint background — light mode */}
      <div className="absolute inset-0 bg-gradient-to-br from-disney-purple-50/40 via-transparent to-disney-gold-50/20 dark:from-disney-purple-950/15 dark:via-transparent dark:to-transparent" />

      <div
        className={`
          w-full max-w-sm px-6 relative z-10 transition-opacity duration-300 ease-out
          ${visible ? 'opacity-100' : 'opacity-0'}
        `}
        role="main"
      >
        {/* Icon + Heading section */}
        <div className="flex flex-col items-center mb-8">
          {/* Paper airplane with sparkle */}
          <div className="relative mb-6">
            <div className="w-20 h-20 rounded-2xl bg-disney-blue-600 dark:bg-disney-blue-600 shadow-glow-purple flex items-center justify-center animate-float">
              {/* <PaperPlaneIcon className="w-10 h-10 text-white" /> */}
              <Timer className="w-8 h-8 text-white" />
            </div>
            {/* Sparkle accent */}
            <div className="absolute -top-1 -right-1 w-6 h-6 animate-sparkle">
              <SparkleIcon className="w-full h-full" />
            </div>
          </div>

          {/* Heading */}
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 text-center mb-2">
            Welcome
          </h1>
          <p className="text-base text-disney-gray-600 dark:text-gray-400 text-center">
            Plan your Disney trip and track queue times
          </p>
        </div>

        {/* Sign-in button */}
        <button
          type="button"
          onClick={handleOAuth}
          className="w-full py-3 px-4 rounded-xl bg-disney-blue-600 hover:bg-disney-blue-400 text-white font-medium flex items-center justify-center gap-2.5 shadow-sm transition-all duration-200 active:scale-[0.98]"
          aria-label="Continue with Google"
        >
          <GoogleIcon className="w-5 h-5 flex-shrink-0" />
          <span>Continue with Google</span>
        </button>

        {/* Trust line — reassurance, not a redirect notice */}
        <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-6">
          Powered by <span className="font-medium text-gray-500 dark:text-gray-400">Disney Queue Time Tracker</span>
        </p>
      </div>
    </div>
  );
}
